/**
 * StandaloneWaiverPage — walk-up waiver signing without a booking (/sign/{org}).
 * Thin wrapper around the shared WaiverFlow (same flow as the customer ticket).
 * Signers can optionally attach a booking confirmation code (?booking=BK-XXX
 * pre-fills it) so their signature also counts on the ticket's group tally.
 * ?kiosk=true (kiosk embed) hides the in-flow branding header — the kiosk
 * chrome already carries the logo + company name.
 * After signing, offers "Sign Another Waiver" so the device is kiosk-ready.
 */
import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Shield, Ticket, X, ChevronDown } from 'lucide-react';
import WaiverFlow from '../components/WaiverFlow';
import api from '../utils/api';

export default function StandaloneWaiverPage() {
  const { orgSlug } = useParams();
  const [searchParams] = useSearchParams();
  const [org, setOrg] = useState(null);
  const [loadingOrg, setLoadingOrg] = useState(true);
  const [loadError, setLoadError] = useState('');
  const isKiosk = searchParams.get('kiosk') === 'true';
  const skipOtp = searchParams.get('verify') === '0'; // operator disabled OTP for the kiosk gate
  const urlBookingCode = (searchParams.get('booking') || '').trim().toUpperCase();
  const [bookingCode, setBookingCode] = useState(urlBookingCode);
  const [codeDraft, setCodeDraft] = useState('');
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [kioskBookingInfo, setKioskBookingInfo] = useState(null);

  // Kiosk gate signing: pull the booking's contact so the OTP step can be
  // skipped — the guest is physically present, contact was just collected.
  useEffect(() => {
    if (!isKiosk || !bookingCode) { setKioskBookingInfo(null); return; }
    let live = true;
    api.get(`/v2/public/bookings/${bookingCode}`)
      .then((r) => {
        const b = r.data?.booking || {};
        if (live) setKioskBookingInfo({ code: bookingCode, email: b.customerEmail || '', phone: b.customerPhone || '' });
      })
      .catch(() => { if (live) setKioskBookingInfo({ code: bookingCode, email: '', phone: '' }); });
    return () => { live = false; };
  }, [isKiosk, bookingCode]);

  useEffect(() => {
    api.get(`/v2/public/org/${orgSlug}/waiver`)
      .then((r) => { setOrg(r.data); setLoadingOrg(false); })
      .catch(() => { setLoadError('Waiver not available or not configured for this organisation.'); setLoadingOrg(false); });
  }, [orgSlug]);

  const handleSubmit = async (p) => {
    const res = await api.post(`/v2/public/org/${orgSlug}/sign-waiver`, {
      signerName: p.signerName,
      signatureData: p.signatureData,
      verified: p.verified,
      verificationMethod: p.verificationMethod,
      verifiedContact: p.verifiedContact,
      phoneNumber: p.phoneNumber,
      email: p.email,
      deviceInfo: p.deviceInfo,
      idVerificationRequired: p.idVerificationRequired || undefined,
      signedAt: p.signedAt,
      waiverVersion: org?.waiverVersion || '1.0',
      ageRange: p.ageRange,
      nationality: p.nationality,
      idNumber: p.idNumber || undefined,
      marketingOptIn: p.marketingOptIn,
      marketingConsentText: p.marketingConsentText,
      smsMarketingOptIn: p.smsMarketingOptIn,
      smsMarketingConsentText: p.smsMarketingConsentText,
      bookingCode: bookingCode || undefined,
    });
    return res.data;
  };

  const applyCode = () => {
    const v = codeDraft.trim().toUpperCase();
    if (v) setBookingCode(v);
  };

  if (loadingOrg) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (loadError) return (
    <div className="min-h-screen flex items-center justify-center p-4 text-center">
      <div className="max-w-sm">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Shield className="w-8 h-8 text-gray-400" />
        </div>
        <p className="text-gray-700 font-medium mb-1">Waiver not available</p>
        <p className="text-gray-500 text-sm mb-4">{loadError}</p>
        <Link to="/" className="text-sm text-blue-600 hover:underline">← BookingFlows</Link>
      </div>
    </div>
  );

  return (
    <div className="h-dvh bg-white flex flex-col overflow-hidden">
      {/* Optional booking association — hidden for POS pre-sign (the booking is
          being created at the counter right now; signatures link at checkout) */}
      {searchParams.get('pos') !== '1' && (
      <div className={`w-full max-w-md mx-auto px-4 flex-shrink-0 ${isKiosk ? 'pt-3' : 'pt-5'}`} data-testid="waiver-booking-association">
        {bookingCode ? (
          <div className="flex items-center justify-between gap-2 bg-blue-50 rounded-2xl px-4 py-3">
            <p className="text-sm text-blue-800 flex items-center gap-2 min-w-0">
              <Ticket className="w-4 h-4 shrink-0" />
              <span className="truncate">Signing for booking <b className="font-mono">{bookingCode}</b></span>
            </p>
            <button
              onClick={() => { setBookingCode(''); setCodeDraft(''); setShowCodeInput(false); }}
              className="text-blue-400 hover:text-blue-700 shrink-0"
              aria-label="Remove booking code"
              data-testid="waiver-booking-code-clear"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-3xl shadow-[0_8px_24px_rgba(0,0,0,0.05)] overflow-hidden">
            <button
              onClick={() => setShowCodeInput((v) => !v)}
              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
              data-testid="waiver-add-booking-code-btn"
            >
              <div className="w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
                <Ticket className="w-4 h-4 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900">Have a booking?</p>
                <p className="text-xs text-gray-500">Add your confirmation code so your signature counts for your group</p>
              </div>
              <ChevronDown className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${showCodeInput ? 'rotate-180' : ''}`} />
            </button>
            {showCodeInput && (
              <div className="px-4 pb-4 border-t border-gray-100">
                <div className="flex gap-2 mt-3">
                  <input
                    type="text"
                    value={codeDraft}
                    onChange={(e) => setCodeDraft(e.target.value.toUpperCase())}
                    onKeyDown={(e) => { if (e.key === 'Enter') applyCode(); }}
                    placeholder="e.g. BK-16BAA4F2"
                    className="flex-1 min-w-0 text-sm border border-gray-200 rounded-xl px-3 py-2.5 font-mono uppercase focus:outline-none focus:ring-2 focus:ring-blue-500"
                    autoFocus
                    data-testid="waiver-booking-code-input"
                  />
                  <button
                    onClick={applyCode}
                    disabled={!codeDraft.trim()}
                    className="px-4 py-2.5 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="waiver-booking-code-apply"
                  >
                    Apply
                  </button>
                </div>
                <p className="text-[11px] text-gray-400 mt-2">It&apos;s on the ticket — skip this if you don&apos;t have one.</p>
              </div>
            )}
          </div>
        )}
      </div>
      )}

      {/* WaiverFlow owns an internal scroll layer (constrained height) — a
          full-height page here would deadlock: the flow's overscroll-contain
          scroll body never overflows, so wheel/touch events die at its boundary
          and the page can't scroll at all (the /sign scroll-lock bug). */}
      <div className="flex-1 min-h-0">
        <WaiverFlow
          layout="embed"
          hideBranding={isKiosk}
        waiverText={org.waiverText}
        branding={{
          name: org.orgName,
          logoUrl: org.logoUrl,
          brandColor: org.primaryColor || '#2563EB',
          waiverVersion: org.waiverVersion,
        }}
        onSubmit={handleSubmit}
        successAction="reset"
        kioskBooking={isKiosk && skipOtp && (bookingCode || searchParams.get('pos') === '1') ? (kioskBookingInfo || { code: bookingCode || '', email: '', phone: '' }) : null}
        posFields={searchParams.get('pos') === '1' ? (org.posFields || null) : null}
        region={
          ['uae', 'united arab emirates', 'ae'].includes((org.country || '').trim().toLowerCase()) ||
          (org.currency || '').trim().toUpperCase() === 'AED'
            ? 'UAE' : 'USA'
        }
        />
      </div>
    </div>
  );
}
